import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlusIcon, LinkIcon, ExclamationTriangleIcon, ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { usePermissions } from '../../auth';
import { fmtTime } from './statusUtils';
import { Pager, usePagination, PageSizeSelect, FilterBar, SearchableSelect, TableEmpty, TableError, FOCUS_RING, FILTER_BTN, FilterCount, FilterActions } from './tableUi';

/**
 * Widen the requested window by this much on each side. The bounds come from the two
 * sightings either side of the gap, so the missed face landed strictly between them —
 * but only if every clock agrees. A couple of minutes of slack costs a few extra cards
 * and saves the operator concluding the face isn't there when it is.
 */
const WINDOW_PAD_SECONDS = 2 * 60;

/**
 * ISO timestamp → UNIX epoch SECONDS, which is what `event_timestamp` stores and what the
 * command-center's `from`/`to` filters compare against. Returns null for anything
 * unparseable so the caller can omit the bound rather than send NaN.
 */
function toEpochSeconds(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return isNaN(ms) ? null : Math.floor(ms / 1000);
}

/** UC2b — register a NEW employee from an unknown face event, or LINK it to an existing one. */
export interface UnknownFacesTabProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
}

export default function UnknownFacesTab({ nodeKey = '' }: UnknownFacesTabProps) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canRegister = hasPermission('attendance.register_employee'); // backend also enforces
  const canLink = hasPermission('attendance.link_employee');
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [action, setAction] = useState<{ kind: 'new' | 'link'; ev: any } | null>(null);
  /** Set when the reader clicks "Show all" — drops the incoming window without navigating. */
  const [ignoreFocus, setIgnoreFocus] = useState(false);

  /**
   * The timeline sends the reader here pointed at the window a missing entry/exit should
   * have fallen in (see TimelineTab's openFaceLog). Narrowing to that window is the whole
   * point of the hand-off: the face they need is one of a handful, not one of a hundred.
   *
   * Bounds are converted to epoch seconds here and handed to the query — see load().
   */
  const focus = useMemo(() => {
    const from = params.get('from');
    const to = params.get('to');
    if (!from && !to) return null;
    // A day-opening exit has no earlier sighting, so the lower bound falls back to the
    // start of the work day rather than being left unbounded.
    const dayStart = params.get('date') ? toEpochSeconds(`${params.get('date')}T00:00:00`) : null;
    const lower = toEpochSeconds(from) ?? dayStart;
    const upper = toEpochSeconds(to);
    return {
      fromSec: lower != null ? lower - WINDOW_PAD_SECONDS : null,
      toSec: upper != null ? upper + WINDOW_PAD_SECONDS : null,
      fromLabel: from ? fmtTime(from) : null,
      toLabel: to ? fmtTime(to) : null,
      name: params.get('name') || '',
      missing: params.get('missing') === 'in' ? 'entry' : 'exit',
      returnTo: params.get('returnTo') || '',
    };
  }, [params]);

  const activeFocus = ignoreFocus ? null : focus;
  // Read off the memo so the fetch depends on the two numbers, not on object identity.
  const fromSec = activeFocus?.fromSec ?? null;
  const toSec = activeFocus?.toSec ?? null;

  /**
   * The window is sent to the API, not applied to the response: the repair flow asks for a
   * few minutes around a missed sighting, and filtering client-side would page the entire
   * feed across the tunnel to discard nearly all of it — worse, it would find nothing at
   * all whenever the window predated the most recent `limit` events.
   */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await attendance.listUnknownFaceEvents({
        // 100 is the command-center's hard ceiling (clampLimit); asking for more just
        // returns 100 and reads as though it fetched everything.
        limit: 100,
        ...(fromSec != null ? { from: fromSec } : {}),
        ...(toSec != null ? { to: toSec } : {}),
      });
      setEvents(Array.isArray(res) ? res : res?.data || res?.events || []);
      setError('');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Request failed');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, nodeKey, fromSec, toSec]);
  useEffect(() => { load(); }, [load]);

  // 24 rather than the tables' 50: these are image cards, and a four-across grid of 50
  // is six screens of scrolling before the pager is reachable.
  const { pageItems, pagerProps, pageSizeProps } = usePagination(events, 24);

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">Unrecognized faces seen by cameras. Register them as a new employee, or link them to someone already enrolled (adds another face angle).</p>

      {activeFocus && (
        <div
          className="mb-4 rounded-lg border border-dashed p-3"
          style={{ borderColor: 'var(--anomaly)', backgroundColor: 'color-mix(in srgb, var(--anomaly) 8%, transparent)' }}
        >
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--anomaly)' }} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                Looking for the missing {activeFocus.missing}
                {activeFocus.name ? <> for <span className="font-semibold">{activeFocus.name}</span></> : null}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {activeFocus.fromLabel
                  ? <>Showing unknown faces seen between {activeFocus.fromLabel} and {activeFocus.toLabel}.</>
                  : <>Showing unknown faces seen before {activeFocus.toLabel}.</>}
                {' '}Link one to them and the timeline pattern repairs itself.
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {activeFocus.returnTo && (
                  <button
                    onClick={() => navigate(activeFocus.returnTo)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border border-border hover:bg-muted transition-colors"
                  >
                    <ArrowLeftIcon className="h-3.5 w-3.5" /> Back to timeline
                  </button>
                )}
                <button
                  onClick={() => setIgnoreFocus(true)}
                  className="px-2 py-1 rounded-md text-[11px] font-medium border border-border hover:bg-muted transition-colors"
                >
                  Show all unknown faces
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FilterBar>
        {/* Server-filtered when a window is active, so this count is already the window's —
            there is no wider total to compare it against without a second query. */}
        <FilterCount>
          {events.length} unknown face{events.length !== 1 ? 's' : ''}{activeFocus ? ' in this window' : ''}
        </FilterCount>
        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          <button onClick={load} className={FILTER_BTN} aria-label="Refresh">
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </FilterActions>
      </FilterBar>

      {loading ? (
        // Same card footprint as a real result, so the grid doesn't reflow when it lands.
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" aria-busy="true" aria-live="polite" aria-label="Loading unknown faces">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-3 shadow-card">
              <div className="aspect-square w-full animate-pulse rounded bg-muted" />
              <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-muted" />
              <div className="mt-1.5 h-3 w-1/2 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : error && events.length === 0 ? (
        <div className="rounded-xl border border-border bg-card shadow-card">
          <TableError message={error} onRetry={load}>Couldn&apos;t load unknown faces</TableError>
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-xl border border-border bg-card shadow-card">
          <TableEmpty>
            {activeFocus
              ? 'No unknown faces in this window — the sighting may never have reached the system at all.'
              : 'No unknown faces. Everyone seen by the cameras was recognised.'}
          </TableEmpty>
        </div>
      ) : (
        // 3 columns at `sm` so a tablet isn't stuck with the phone's 2-up layout.
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {pageItems.map((ev) => (
            <div key={ev.id || ev.event_id} className="rounded-lg border border-border bg-card p-3 shadow-card transition-shadow hover:shadow-card-hover">
              <FaceThumb eventId={ev.id || ev.event_id} />
              <div className="mt-2 text-xs text-muted-foreground">{fmtTime(ev.created_at || (ev.event_timestamp ? new Date(ev.event_timestamp * 1000).toISOString() : null))}</div>
              <div className="text-xs text-muted-foreground">conf {ev.confidence != null ? Number(ev.confidence).toFixed(2) : '—'}</div>
              <div className="mt-2 flex gap-1.5">
                {canRegister && (
                  <button onClick={() => setAction({ kind: 'new', ev })} className={`inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-brand/10 px-2 py-1 text-[11px] font-medium text-brand transition-colors hover:bg-brand/20 ${FOCUS_RING}`} title="Register this face as a new employee">
                    <UserPlusIcon className="h-3.5 w-3.5" /> Register
                  </button>
                )}
                {canLink && (
                  <button onClick={() => setAction({ kind: 'link', ev })} className={`inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-muted ${FOCUS_RING}`} title="Link this face to an existing employee">
                    <LinkIcon className="h-3.5 w-3.5" /> Link
                  </button>
                )}
                {!canRegister && !canLink && <span className="flex-1 py-1 text-center text-[11px] text-muted-foreground">View only</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {events.length > 0 && (
        <div className="mt-4 rounded-xl border border-border bg-card shadow-card overflow-hidden">
          <Pager {...pagerProps} />
        </div>
      )}
      {action?.kind === 'new' && <RegisterNewModal ev={action.ev} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />}
      {action?.kind === 'link' && <LinkModal ev={action.ev} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />}
    </div>
  );
}

function FaceThumb({ eventId }: { eventId: string }) {
  const { attendance } = useApi();
  const [src, setSrc] = useState<string | null>(null);
  // Prefer the aligned face crop; fall back to the plain face crop for events whose
  // pipeline didn't emit an alignedFace image (older events / AI modules that don't produce one).
  useEffect(() => {
    let alive = true;
    (async () => {
      let u = await attendance.eventImageDataUrl(eventId, 'alignedFace');
      if (!u) u = await attendance.eventImageDataUrl(eventId, 'faceCrop');
      if (alive) setSrc(u);
    })();
    return () => { alive = false; };
  }, [attendance, eventId]);
  return src
    ? <img src={src} alt="face" className="w-full aspect-square object-cover rounded border border-border" />
    : <div className="w-full aspect-square rounded border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">no image</div>;
}

function Modal({ children, onClose }: { children: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-modal border border-border" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function RegisterNewModal({ ev, onClose, onDone }: { ev: any; onClose: () => void; onDone: () => void }) {
  const { attendance } = useApi();
  const [form, setForm] = useState<any>({ name: '', employee_code: '', department: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    try { await attendance.createEmployeeFromEvent({ ...form, event_id: ev.event_id }); onDone(); } catch (e: any) { setErr(e.message || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-semibold text-foreground mb-4">Register as new employee</h3>
      {['name', 'employee_code', 'department'].map((k) => (
        <label key={k} className="block mb-3">
          <span className="text-xs text-muted-foreground capitalize">{k.replace('_', ' ')}{k === 'name' ? ' *' : ''}</span>
          <input value={form[k]} onChange={(e) => setForm((f: any) => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground" />
        </label>
      ))}
      {err && <div className="text-sm text-destructive">{err}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="px-3 py-2 rounded-lg bg-brand text-white hover:bg-brand-dark text-sm disabled:opacity-50">{busy ? 'Saving…' : 'Register'}</button>
      </div>
    </Modal>
  );
}

function LinkModal({ ev, onClose, onDone }: { ev: any; onClose: () => void; onDone: () => void }) {
  const { attendance } = useApi();
  const [employees, setEmployees] = useState<any[]>([]);
  const [sel, setSel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { (async () => { try { const r = await attendance.listEmployees({ limit: 200 }); setEmployees(Array.isArray(r) ? r : []); } catch { /* ignore */ } })(); }, [attendance]);
  const submit = async () => {
    if (!sel) { setErr('Pick an employee'); return; }
    setBusy(true); setErr('');
    try { await attendance.linkEventToEmployee(sel, ev.event_id); onDone(); } catch (e: any) { setErr(e.message || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-semibold text-foreground mb-4">Link to existing employee</h3>
      <SearchableSelect
        value={sel}
        onChange={setSel}
        placeholder="Select employee…"
        searchPlaceholder="Search employee…"
        options={employees.map((e) => ({ value: e.id, label: `${e.name}${e.employee_code ? ` (${e.employee_code})` : ''}` }))}
      />
      {err && <div className="text-sm text-destructive mt-3">{err}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="px-3 py-2 rounded-lg bg-brand text-white hover:bg-brand-dark text-sm disabled:opacity-50">{busy ? 'Linking…' : 'Link'}</button>
      </div>
    </Modal>
  );
}
